import React from "react";

/**
 * Language for the Tests & Tools page.
 *
 * Page-local by design — exactly as the old /tests hub did it (see
 * Component/HubShell.js): switching to Arabic here must not flip the rest of
 * the site. The choice is remembered on the device and can be forced with
 * ?lang=ar so a link can open the page in Arabic.
 *
 * The عربي chip lives in the shared LandingNav while the page that reacts to
 * it is a separate tree, so the state is shared through this module and a
 * custom event rather than props.
 */
export const TT_LANG_KEY = "ulearn-tt-lang";
export const TT_LANG_EVENT = "ulearn:tt-lang";

const clean = (v) => (v === "ar" || v === "en" ? v : null);

export function readTtLang() {
  try {
    const fromUrl = clean(new URLSearchParams(window.location.search).get("lang"));
    if (fromUrl) return fromUrl;
    return clean(localStorage.getItem(TT_LANG_KEY)) || "en";
  } catch {
    return "en";
  }
}

export function setTtLang(lang) {
  const next = clean(lang) || "en";
  try {
    localStorage.setItem(TT_LANG_KEY, next);
  } catch {
    /* private mode — the page still switches for this visit */
  }
  window.dispatchEvent(new Event(TT_LANG_EVENT));
  return next;
}

export const toggleTtLang = () => setTtLang(readTtLang() === "ar" ? "en" : "ar");

/** Live language for the page, kept in step with the nav chip and other tabs. */
export function useTtLang() {
  const [lang, setLang] = React.useState(readTtLang);
  React.useEffect(() => {
    const sync = () => setLang(readTtLang());
    window.addEventListener(TT_LANG_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(TT_LANG_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  return lang;
}
